import { useEffect, useRef } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { Scale } from 'lucide-react'
import { MessageBubble } from './MessageBubble'
import { Composer } from './Composer'
import type { ComposerHandle } from './Composer'
import type { AttachedDoc, ChatTurn } from '../lib/types'
import type { PendingTurn } from '../hooks/useChat'
import { useI18n } from '../lib/i18n'

interface Props {
  history: ChatTurn[]
  pending: PendingTurn | null
  error: string | null
  isStreaming: boolean
  onSend: (text: string) => void
  onStop: () => void
  attachedDocs: AttachedDoc[]
  onAttachmentsChange: Dispatch<SetStateAction<AttachedDoc[]>>
  onDeleteTurn?: (turnIndex: number) => void
}

export function ChatWindow({
  history,
  pending,
  error,
  isStreaming,
  onSend,
  onStop,
  attachedDocs,
  onAttachmentsChange,
  onDeleteTurn,
}: Props) {
  const { s } = useI18n()
  const bottomRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<ComposerHandle>(null)

  // Stick to the bottom as new content arrives (or streams in).
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [history.length, pending?.partial, pending?.sources.length])

  const isEmpty = history.length === 0 && !pending

  return (
    <main className="flex-1 flex flex-col h-screen bg-brand-50 min-w-0">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          {isEmpty ? (
            <EmptyState />
          ) : (
            <>
              {history.map((turn, i) => (
                <MessageBubble
                  key={i}
                  role={turn.role}
                  content={turn.content}
                  reasoning={turn.reasoning}
                  steps={turn.steps}
                  sources={turn.sources}
                  stats={turn.stats}
                  verification={turn.verification}
                  onDelete={onDeleteTurn ? () => onDeleteTurn(i) : undefined}
                  onFillInput={(q) => composerRef.current?.fill(q)}
                />
              ))}
              {pending && (
                <>
                  <MessageBubble role="user" content={pending.question} />
                  <MessageBubble
                    role="assistant"
                    content={pending.partial}
                    reasoning={pending.reasoning}
                    steps={pending.steps}
                    sources={pending.sources}
                    stats={pending.stats}
                    verification={pending.verification}
                    isStreaming
                  />
                </>
              )}
              {error && (
                <div className="my-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <span className="font-semibold">{s.errorLabel}:</span> {error}
                </div>
              )}
              <div ref={bottomRef} />
            </>
          )}
        </div>
      </div>

      <Composer
        ref={composerRef}
        onSend={onSend}
        onStop={onStop}
        isStreaming={isStreaming}
        attachedDocs={attachedDocs}
        onAttachmentsChange={onAttachmentsChange}
      />
    </main>
  )
}

function EmptyState() {
  const { s } = useI18n()
  return (
    <div className="text-center py-20 text-brand-500 max-w-xl mx-auto">
      <Scale className="w-12 h-12 mx-auto mb-4 text-brand-400" />
      <h2 className="text-xl font-semibold text-brand-800 mb-2">
        {s.appNameLong}
      </h2>
      <p className="text-sm leading-relaxed">
        {s.emptyDescBefore}
        <span className="font-mono text-brand-700">{s.newChat}</span>
        {s.emptyDescAfter}
      </p>
      <div className="mt-6 grid gap-2 text-left">
        {s.examples.map((text, i) => (
          <ExampleQuery key={i} text={text} />
        ))}
      </div>
    </div>
  )
}

function ExampleQuery({ text }: { text: string }) {
  return (
    <div className="text-xs text-brand-400 bg-white border border-brand-200 rounded-lg px-3 py-2 leading-snug">
      💭 {text}
    </div>
  )
}
